import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { registerPatient } from '../lib/auth';
import { t } from '../i18n';
import { Button, Input, Select, Field, Alert, Spinner } from '../components/ui';

/**
 * Patient self-registration.
 *
 * Three steps: personal details, coverage, professional. Coverage is required
 * (either an obra social / prepaga with its subcategoría, or Particular), so a
 * session always has a value to bill against.
 *
 * The lists here are read anonymously — see migration 0004, which grants the
 * anon role exactly these rows and columns and nothing else.
 */

const STEPS = ['personal', 'coverage', 'professional'];

const emptyForm = {
  firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
  dateOfBirth: '', idNumber: '', nationality: 'Argentina', address: '', phone: '',
  emergencyName: '', emergencyPhone: '', emergencyRelation: '', consentSigned: false,
  // On by default: someone who books a session wants it in their calendar.
  // Opting out here or later from Mi perfil is one checkbox either way.
  wantsCalendarInvites: true,
};

const RegisterView = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [companyId, setCompanyId] = useState('');
  const [planId, setPlanId] = useState('');
  const [isParticular, setIsParticular] = useState(false);
  const [therapistId, setTherapistId] = useState('');

  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const navigate = useNavigate();
  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  useEffect(() => {
    const load = async () => {
      const [companyRows, therapistRows] = await Promise.all([
        supabase.from('insurance_companies').select('id,name').eq('is_active', true).order('name'),
        supabase.from('therapists').select('id,first_name,last_name').eq('is_active', true).order('last_name'),
      ]);
      setCompanies(companyRows.data ?? []);
      setTherapists(therapistRows.data ?? []);
      setLoadingLists(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (!companyId) { setPlans([]); setPlanId(''); return; }
    supabase
      .from('insurance_plans')
      .select('id,name')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => setPlans(data ?? []));
  }, [companyId]);

  const validatePersonal = () => {
    const required = ['firstName', 'lastName', 'email', 'password', 'dateOfBirth', 'idNumber',
      'nationality', 'address', 'phone', 'emergencyName', 'emergencyPhone', 'emergencyRelation'];
    if (required.some((field) => !form[field]?.trim?.())) return t('register.fillRequired');
    if (form.password.length < 8) return t('register.passwordTooShort');
    if (form.password !== form.confirmPassword) return t('register.passwordMismatch');
    if (!form.consentSigned) return t('register.consentRequired');
    return null;
  };

  const handleNext = () => {
    if (step === 0) {
      const problem = validatePersonal();
      if (problem) { setError(problem); return; }
    }
    if (step === 1 && !isParticular && !planId) { setError(t('register.coverageRequired')); return; }
    setError(null);
    setStep((current) => current + 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    const { error: registerError, pendingConfirmation } = await registerPatient({
      email: form.email.trim(),
      password: form.password,
      profile: { ...form, email: form.email.trim() },
      therapistId: therapistId || null,
      insurancePlanId: isParticular ? null : planId || null,
    });

    if (registerError) {
      setError(registerError.message);
      setSubmitting(false);
      return;
    }
    setDone(pendingConfirmation ? 'confirm' : 'ready');
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">{t('register.successTitle')}</h1>
          <p className="mt-2 text-sm text-slate-600">
            {done === 'confirm' ? t('register.checkEmail') : t('register.readyToLogin')}
          </p>
          <Button className="mt-6 w-full" onClick={() => navigate('/ingresar')}>
            {t('auth.login')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
      <div className="mx-auto my-8 max-w-2xl">
        <h1 className="mb-1 text-center text-2xl font-bold text-indigo-900">{t('register.title')}</h1>
        <p className="mb-6 text-center text-sm text-slate-600">{t('register.subtitle')}</p>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex gap-2">
            {STEPS.map((name, index) => (
              <div key={name} className="flex-1">
                <div className={`h-1.5 rounded-full ${index <= step ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                <p className={`mt-1.5 text-xs ${index <= step ? 'font-medium text-indigo-700' : 'text-slate-400'}`}>
                  {t(`register.step.${name}`)}
                </p>
              </div>
            ))}
          </div>

          {error && <div className="mb-4"><Alert tone="danger">{error}</Alert></div>}

          {step === 0 && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('register.firstName')} required>
                  <Input value={form.firstName} onChange={(e) => update({ firstName: e.target.value })} />
                </Field>
                <Field label={t('register.lastName')} required>
                  <Input value={form.lastName} onChange={(e) => update({ lastName: e.target.value })} />
                </Field>
              </div>
              <Field label={t('auth.email')} required>
                <Input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('auth.password')} required hint={t('register.passwordHint')}>
                  <Input type="password" value={form.password} onChange={(e) => update({ password: e.target.value })} />
                </Field>
                <Field label={t('register.confirmPassword')} required>
                  <Input type="password" value={form.confirmPassword} onChange={(e) => update({ confirmPassword: e.target.value })} />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('patients.dateOfBirth')} required>
                  <Input type="date" value={form.dateOfBirth} onChange={(e) => update({ dateOfBirth: e.target.value })} />
                </Field>
                <Field label={t('patients.idNumber')} required>
                  <Input value={form.idNumber} onChange={(e) => update({ idNumber: e.target.value })} />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('register.nationality')} required>
                  <Input value={form.nationality} onChange={(e) => update({ nationality: e.target.value })} />
                </Field>
                <Field label={t('register.phone')} required>
                  <Input type="tel" value={form.phone} onChange={(e) => update({ phone: e.target.value })} />
                </Field>
              </div>
              <Field label={t('register.address')} required>
                <Input value={form.address} onChange={(e) => update({ address: e.target.value })} />
              </Field>

              <h3 className="border-t border-slate-100 pt-4 font-semibold text-slate-800">
                {t('register.emergencyContact')}
              </h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label={t('register.firstName')} required>
                  <Input value={form.emergencyName} onChange={(e) => update({ emergencyName: e.target.value })} />
                </Field>
                <Field label={t('register.phone')} required>
                  <Input type="tel" value={form.emergencyPhone} onChange={(e) => update({ emergencyPhone: e.target.value })} />
                </Field>
                <Field label={t('patients.relationship')} required>
                  <Input value={form.emergencyRelation} onChange={(e) => update({ emergencyRelation: e.target.value })} />
                </Field>
              </div>

              <label className="flex items-start gap-2 rounded-lg bg-indigo-50 p-4">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.consentSigned}
                  onChange={(e) => update({ consentSigned: e.target.checked })}
                />
                <span className="text-sm text-slate-700">{t('register.consentText')}</span>
              </label>

              <label className="flex items-start gap-2 px-1">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.wantsCalendarInvites}
                  onChange={(e) => update({ wantsCalendarInvites: e.target.checked })}
                />
                <span className="text-sm text-slate-600">{t('profile.calendarInvites')}</span>
              </label>
            </div>
          )}

          {step === 1 && (
            loadingLists ? <Spinner /> : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">{t('register.coverageIntro')}</p>

                <button
                  type="button"
                  onClick={() => { setIsParticular(true); setCompanyId(''); setPlanId(''); }}
                  className={`w-full rounded-lg border-2 p-4 text-left transition ${
                    isParticular ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <p className="font-semibold text-slate-900">{t('insurance.particular')}</p>
                  <p className="text-sm text-slate-600">{t('register.particularHint')}</p>
                </button>

                <div className={`rounded-lg border-2 p-4 transition ${
                  !isParticular && companyId ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200'
                }`}>
                  <p className="mb-3 font-semibold text-slate-900">{t('insurance.company')}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label={t('insurance.company')}>
                      <Select
                        value={companyId}
                        onChange={(e) => { setCompanyId(e.target.value); setIsParticular(false); }}
                      >
                        <option value="">{t('register.selectCompany')}</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>{company.name}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('insurance.plan')}>
                      <Select
                        value={planId}
                        onChange={(e) => setPlanId(e.target.value)}
                        disabled={!companyId}
                      >
                        <option value="">{t('register.selectPlan')}</option>
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>{plan.name}</option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">{t('register.coverageMissingHint')}</p>
                </div>
              </div>
            )
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{t('register.professionalIntro')}</p>
              <button
                type="button"
                onClick={() => setTherapistId('')}
                className={`w-full rounded-lg border-2 p-4 text-left transition ${
                  !therapistId ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className="font-semibold text-slate-900">{t('register.chooseLater')}</p>
              </button>
              {therapists.map((therapist) => (
                <button
                  key={therapist.id}
                  type="button"
                  onClick={() => setTherapistId(therapist.id)}
                  className={`w-full rounded-lg border-2 p-4 text-left transition ${
                    therapistId === therapist.id ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <p className="font-semibold text-slate-900">
                    {therapist.first_name} {therapist.last_name}
                  </p>
                </button>
              ))}
              <p className="text-xs text-slate-500">{t('register.approvalNote')}</p>
            </div>
          )}

          <div className="mt-6 flex gap-3 border-t border-slate-100 pt-5">
            {step > 0 && (
              <Button variant="secondary" onClick={() => { setError(null); setStep((s) => s - 1); }} disabled={submitting}>
                {t('common.back')}
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button onClick={handleNext} className="flex-1">{t('common.next')}</Button>
            ) : (
              <Button variant="success" onClick={handleSubmit} loading={submitting} className="flex-1">
                {t('register.submit')}
              </Button>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-sm text-slate-600">
          {t('register.haveAccount')}{' '}
          <Link to="/ingresar" className="font-semibold text-indigo-600 hover:underline">
            {t('auth.login')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterView;
